import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

interface SuccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  autoCloseSeconds?: number;
}

export const SuccessDialog: React.FC<SuccessDialogProps> = ({
  isOpen,
  onClose,
  title = 'Success!',
  description = '',
  autoCloseSeconds = 3,
}) => {
  const [isShowing, setIsShowing] = useState(isOpen);

  useEffect(() => {
    setIsShowing(isOpen);
    if (isOpen) {
      setTimeout(() => {
        setIsShowing(false);
        onClose();
      }, autoCloseSeconds * 1000);
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isShowing && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50"
            onClick={onClose}
          />

          {/* Dialog */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.8,
                y: 20,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                scale: 0.9,
                y: 10,
              }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
                duration: 0.3,
              }}
              className="relative bg-gray/80 dark:bg-slate-900/95 backdrop-blur-xl border border-gray-400/40 dark:border-slate-600/40 rounded-3xl p-8 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}>
              {/* Glass effect overlay */}
              <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-br from-gray-300/30 via-transparent to-gray-100/10 dark:from-gray-100/10 dark:via-transparent dark:to-gray-50/5 pointer-events-none" />

              {/* Content */}
              <div className="relative z-10 text-center space-y-6">
                {/* Animated Check Circle */}
                <div className="flex justify-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: 'spring',
                      stiffness: 200,
                      damping: 20,
                      delay: 0.2,
                    }}
                    className="relative">
                    {/* Outer glow ring */}
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: [0, 1.2, 1], opacity: [0, 0.6, 0.3] }}
                      transition={{
                        duration: 0.8,
                        delay: 0.3,
                        ease: 'easeOut',
                      }}
                      className="absolute inset-0 w-32 h-32 bg-blue-500/20 rounded-full blur-sm"
                    />

                    {/* Circle border container */}
                    <div className="relative w-32 h-32">
                      {/* Animated circle stroke */}
                      <svg
                        className="absolute inset-0 w-32 h-32 transform -rotate-90"
                        viewBox="0 0 100 100">
                        {/* Background circle */}
                        <circle
                          cx="50"
                          cy="50"
                          r="45"
                          fill="none"
                          stroke="rgb(59 130 246 / 0.2)"
                          strokeWidth="3"
                        />
                        {/* Animated progress circle */}
                        <motion.circle
                          cx="50"
                          cy="50"
                          r="45"
                          fill="none"
                          stroke="rgb(59 130 246)"
                          strokeWidth="3"
                          strokeLinecap="round"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{
                            duration: 1.2,
                            delay: 0.3,
                            ease: 'easeInOut',
                          }}
                          style={{
                            strokeDasharray: '283', // 2 * PI * 45
                            strokeDashoffset: 0,
                          }}
                        />
                      </svg>

                      {/* Inner filled circle background */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                          delay: 0.5,
                          duration: 0.3,
                          ease: 'easeOut',
                        }}
                        className="absolute inset-2 bg-gradient-to-br from-blue-500 to-blue-800 rounded-full shadow-lg shadow-blue-500/25"
                      />

                      {/* Checkmark SVG */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{
                          delay: 0.6,
                          duration: 0.3,
                          type: 'spring',
                          stiffness: 300,
                          damping: 20,
                        }}
                        className="absolute inset-0 flex items-center justify-center">
                        <svg
                          className="w-32 h-32 text-white"
                          viewBox="0 0 24 24"
                          fill="none">
                          <motion.path
                            d="M8 12.5l3 3 5-5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{
                              delay: 0.7,
                              duration: 0.3,
                              ease: 'easeInOut',
                            }}
                          />
                        </svg>
                      </motion.div>
                    </div>

                    {/* Continuous pulse effect */}
                    <motion.div
                      initial={{ scale: 1, opacity: 0 }}
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0, 0.3, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: 2.2,
                      }}
                      className="absolute inset-0 w-32 h-32 bg-blue-500/20 rounded-full"
                    />
                  </motion.div>
                </div>

                {/* Title */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.4 }}
                  className="space-y-3">
                  <h3 className="text-2xl font-semibold text-slate-800 dark:text-slate-200 tracking-wide">
                    {title}
                  </h3>
                  {description && (
                    <p className="text-slate-600 dark:text-slate-400 text-md leading-relaxed">
                      {description}
                    </p>
                  )}
                </motion.div>

                {/* Progress indicator */}
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: '100%' }}
                  transition={{
                    delay: 1,
                    duration: 2,
                  }}
                  className="h-1 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full mx-auto"
                  style={{ maxWidth: '150px' }}
                />
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SuccessDialog;
